import {
  AdmissionsCriteriaItem,
  CriteriaCategory,
  groupCriteria,
  matchStrengthRank,
} from 'components/shared/ai/AdmissionsCriteria';
import { AICriteriaCategorySnapshot, AIJobCriteria } from 'models/AIJob';
import { labelOf } from 'services/api/legacyCriterionKey';

// Group an admission job's evaluated criteria by category for the criteria drawer, ordered by the
// configured category order, then by match strength within the category, then by the configured
// criteria order. Delegates the grouping + color validation to the shared `groupCriteria` (also used
// by the referral clinical assessment), and the strength ranking to the shared `matchStrengthRank` —
// so a category reads strongest-evidenced first on both surfaces. The configured criteria order
// breaks ties within a strength tier, and still fully determines the order of a job whose criteria
// predate the evidence enrichment and carry no strength at all.
//
// No matched-ness filter here: the API serializes only flagged (matched) criteria on the job, so
// every entry it returns is already matched.
export function groupCriteriaByCategory(criteria: AIJobCriteria[]): CriteriaCategory[] {
  const ordered = [...criteria].sort(
    (a, b) =>
      categoryOf(a).order - categoryOf(b).order ||
      matchStrengthRank(a.matchStrength) - matchStrengthRank(b.matchStrength) ||
      a.aiCriteria.order - b.aiCriteria.order
  );

  return groupCriteria(
    ordered.map((c) => {
      const category = categoryOf(c);
      return {
        categoryId: category.id,
        categoryName: category.name,
        categoryColor: category.color,
        criteria: criteriaFrom(c),
      };
    })
  );
}

// OLIO-2697 rename transition: prefer the new `aiCriteriaCategory` snapshot, fall back to the
// legacy `aiCriteriaSetCategory`. Test the new key for content rather than truthiness — rows
// written during the api rollout window carry the jsonb column default, {}, which is truthy, so
// `??` and `||` would both skip the fallback. OLIO-2698 drops the legacy key and this guard.
function categoryOf(c: AIJobCriteria): AICriteriaCategorySnapshot {
  return c.aiCriteriaCategory?.name ? c.aiCriteriaCategory : c.aiCriteriaSetCategory;
}

function criteriaFrom(c: AIJobCriteria): AdmissionsCriteriaItem {
  return {
    label: labelOf(c.aiCriteria),
    description: c.aiCriteria.description || undefined,
    finding: c.finding || undefined,
    matchStrength: c.matchStrength || undefined,
    sourceCount: c.sources.length,
    sources: c.sources.map((s) => ({ attachmentId: s.attachmentId, displayId: s.displayId })),
  };
}
